/**
 * TestCasesPage — Test Cases Module v2 (Phase 2)
 *
 * Layout strategy:
 *
 *   Desktop (md+): three-pane split
 *   ┌──────────────┬────────────────────┬──────────────────┐
 *   │ FolderTree   │ TestCaseList       │ TestCaseDetail   │
 *   │ (240px)      │ (flex-1)           │ (400px)          │
 *   └──────────────┴────────────────────┴──────────────────┘
 *
 *   Mobile: single pane with overlays
 *   - FolderTree → slide-over drawer from left (toggled by toolbar button)
 *   - TestCaseDetail → fixed fullscreen overlay (opens when case selected)
 *
 * All AI features from v1 (suite generation, spec-to-test, optimizer)
 * are preserved as independent modals.
 *
 * Shared UI state lives in useTestCasesStore (Zustand).
 */

import { useState, useMemo, useEffect, useRef } from 'react';
import { useSearchParams } from 'react-router-dom';
import { useQueryClient } from '@tanstack/react-query';
import axios from 'axios';
import {
    ClipboardList, Plus, Loader2, Sparkles, Search, Download,
    X, PanelLeft,
} from 'lucide-react';

import { useAuth }                    from '../context/AuthContext';
import { useOrganizationFeatures }    from '../hooks/useOrganizationFeatures';
import { useProjectContext }          from '../context/ProjectContext';
import { useTestCasesStore }          from '../store/useTestCasesStore';
import { useTestCaseList }            from '../components/test-cases/TestCaseList/useTestCaseList';
import { useFolderTree }              from '../components/test-cases/FolderTree/useFolderTree';
import { FolderTree }                 from '../components/test-cases/FolderTree/FolderTree';
import { TestCaseList }               from '../components/test-cases/TestCaseList/TestCaseList';
import { TestCaseDetail }             from '../components/test-cases/TestCaseDetail/TestCaseDetail';
import { TestCaseFullScreenModal }    from '../components/test-cases/TestCaseDetail/TestCaseFullScreenModal';
import { BulkActionsBar }             from '../components/BulkActionsBar';
import { DndProvider }               from '../components/test-cases/shared/DndProvider';
import type { DragEndPayload, DragOverPayload } from '../components/test-cases/shared/DndProvider';
import { OptimizedTestCasesModal, type IOptimizedTestCase, type WritingStyle } from '../components/OptimizedTestCasesModal';
import { OptimizeSetupModal }         from '../components/OptimizeSetupModal';
import { SpecToTestModal }            from '../components/SpecToTestModal';
import { NEW_TEST_CASE_ID }           from '../components/test-cases/types';
import { TestCaseFilterBar }          from '../components/test-cases/shared/TestCaseFilterBar';
import type { TabId }                from '../components/test-cases/TestCaseDetail/TestCaseDetail';
import { API_BASE_URL } from '../config/api';

const API_URL = API_BASE_URL;
const VALID_TABS: TabId[] = ['details', 'steps', 'comments', 'history'];

// ── Component ─────────────────────────────────────────────────────────────────

export function TestCasesPage() {
    const { token, user }   = useAuth();
    const queryClient       = useQueryClient();
    const { aiFeatures }    = useOrganizationFeatures();
    const isViewer          = user?.role === 'viewer';

    const [searchParams, setSearchParams] = useSearchParams();
    const { activeProjectId: ctxProjectId, setActiveProjectId: setCtxProjectId } = useProjectContext();

    // ── Project selection (URL + context sync) ───────────────────────────────
    const selectedProjectId = searchParams.get('project') ?? ctxProjectId ?? '';
    const setSelectedProjectId = (id: string) => {
        setSearchParams((prev) => {
            const next = new URLSearchParams(prev);
            if (id) next.set('project', id); else next.delete('project');
            return next;
        }, { replace: true });
        setCtxProjectId(id);
    };
    useEffect(() => {
        if (ctxProjectId !== searchParams.get('project')) {
            setSearchParams((prev) => {
                const next = new URLSearchParams(prev);
                if (ctxProjectId) next.set('project', ctxProjectId); else next.delete('project');
                return next;
            }, { replace: true });
        }
    }, [ctxProjectId]);

    // ── Zustand store ────────────────────────────────────────────────────────
    const selectedFolderId       = useTestCasesStore((s) => s.selectedFolderId);
    const setSelectedFolderId    = useTestCasesStore((s) => s.setSelectedFolderId);
    const selectedTestCaseId     = useTestCasesStore((s) => s.selectedTestCaseId);
    const setSelectedTestCaseId  = useTestCasesStore((s) => s.setSelectedTestCaseId);
    const selectedIds            = useTestCasesStore((s) => s.selectedIds);
    const clearSelection         = useTestCasesStore((s) => s.clearSelection);
    const searchQuery            = useTestCasesStore((s) => s.searchQuery);
    const setSearchQuery         = useTestCasesStore((s) => s.setSearchQuery);
    const reset                  = useTestCasesStore((s) => s.reset);
    const detailMode             = useTestCasesStore((s) => s.detailMode);
    const setDetailMode          = useTestCasesStore((s) => s.setDetailMode);

    // ── Derived from store — must be declared before any useEffect that uses it ──
    const isFullscreen = detailMode === 'fullscreen';

    // ── Active tab (controlled — synced to URL) — declared before URL-sync effect ──
    const [activeTab, setActiveTab] = useState<TabId>('details');

    // Reset store on page unmount to prevent stale state on re-navigation
    useEffect(() => () => reset(), []);

    // ── URL ↔ Zustand deep-link sync ─────────────────────────────────────────
    // Phase 1 (mount only): hydrate Zustand from URL params so a refreshed or
    // shared URL restores the selected folder + test case.
    const hasHydratedUrl = useRef(false);
    useEffect(() => {
        if (hasHydratedUrl.current) return;
        hasHydratedUrl.current = true;
        const urlFolder     = searchParams.get('folder');
        const urlTestcase   = searchParams.get('testcase');
        const urlFullscreen = searchParams.get('fullscreen') === 'true';
        const urlTab        = searchParams.get('tab') as TabId | null;
        if (urlFolder)   setSelectedFolderId(urlFolder);
        if (urlTestcase) setSelectedTestCaseId(urlTestcase);
        if (urlFullscreen && urlTestcase) setDetailMode('fullscreen');
        if (urlTab && VALID_TABS.includes(urlTab)) setActiveTab(urlTab);
    }, []); // eslint-disable-line react-hooks/exhaustive-deps

    // Phase 2 (ongoing): write Zustand state + local tab/fullscreen state back to
    // the URL so every interaction updates the address bar and enables deep linking.
    useEffect(() => {
        setSearchParams((prev) => {
            const desiredFolder     = selectedFolderId ?? '';
            const desiredTestcase   = (selectedTestCaseId && selectedTestCaseId !== NEW_TEST_CASE_ID)
                ? selectedTestCaseId : '';
            const desiredFullscreen = isFullscreen && !!desiredTestcase ? 'true' : '';
            const desiredTab        = desiredTestcase && activeTab !== 'details' ? activeTab : '';
            // Skip navigation if nothing changed.
            if ((prev.get('folder')     ?? '') === desiredFolder &&
                (prev.get('testcase')   ?? '') === desiredTestcase &&
                (prev.get('fullscreen') ?? '') === desiredFullscreen &&
                (prev.get('tab')        ?? '') === desiredTab) {
                return prev;
            }
            const next = new URLSearchParams(prev);
            if (desiredFolder)     next.set('folder',     desiredFolder);     else next.delete('folder');
            if (desiredTestcase)   next.set('testcase',   desiredTestcase);   else next.delete('testcase');
            if (desiredFullscreen) next.set('fullscreen', 'true');            else next.delete('fullscreen');
            if (desiredTab)        next.set('tab',        desiredTab);        else next.delete('tab');
            return next;
        }, { replace: true });
    }, [selectedFolderId, selectedTestCaseId, isFullscreen, activeTab]); // eslint-disable-line react-hooks/exhaustive-deps

    // ── Reorder mode ─────────────────────────────────────────────────────────
    const [isReorderMode, setIsReorderMode] = useState(false);

    // ── Mobile overlay state ─────────────────────────────────────────────────
    const [mobileFolderOpen, setMobileFolderOpen] = useState(false);

    // Auto-close mobile folder drawer when a folder is selected
    useEffect(() => { setMobileFolderOpen(false); }, [selectedFolderId]);

    // ── Projects ─────────────────────────────────────────────────────────────
    const [projects, setProjects] = useState<Array<{ id: string; name: string }>>([]);
    useEffect(() => {
        if (!token) return;
        axios.get(`${API_URL}/api/projects`, { headers: { Authorization: `Bearer ${token}` } })
            .then(({ data }) => setProjects(data.projects ?? []))
            .catch(() => { /* silent */ });
    }, [token]);

    const effectiveProjectId = selectedProjectId || '';

    // ── Folders (for BulkActionsBar "Move to Folder" + folder drag) ─────────
    const { folders, moveFolder } = useFolderTree(effectiveProjectId || null);

    // ── Test cases ───────────────────────────────────────────────────────────
    const { testCases, allTestCases, isLoading, isError, invalidate, bulkDelete, moveToFolder, bulkPatch, reorder } =
        useTestCaseList({ projectId: effectiveProjectId || null, folderId: selectedFolderId });

    // Find the selected test case from the already-fetched list
    const selectedTestCase = useMemo(
        () => (selectedTestCaseId && selectedTestCaseId !== NEW_TEST_CASE_ID
            ? (testCases.find((tc) => tc._id === selectedTestCaseId) ??
               allTestCases.find((tc) => tc._id === selectedTestCaseId) ??
               null)
            : null),
        [selectedTestCaseId, testCases, allTestCases],
    );

    // ── Search debounce ──────────────────────────────────────────────────────
    const [searchInput, setSearchInput]       = useState(searchQuery);
    const debounceRef = useRef<ReturnType<typeof setTimeout> | null>(null);
    function handleSearchChange(value: string) {
        setSearchInput(value);
        if (debounceRef.current) clearTimeout(debounceRef.current);
        debounceRef.current = setTimeout(() => setSearchQuery(value), 300);
    }

    // ── Bulk selection helpers ────────────────────────────────────────────────
    const selectedIdsArray = useMemo(() => [...selectedIds], [selectedIds]);
    const selectedManualIds = useMemo(() => {
        const manualSet = new Set(allTestCases.filter((tc) => tc.type === 'MANUAL').map((tc) => tc._id));
        return selectedIdsArray.filter((id) => manualSet.has(id));
    }, [selectedIdsArray, allTestCases]);

    async function handleBulkDelete() {
        const automatedCount = allTestCases.filter((tc) => selectedIds.has(tc._id) && tc.type === 'AUTOMATED').length;
        if (automatedCount > 0 && !window.confirm(
            `${automatedCount} of the selected test cases are AUTOMATED type. Deleting them will not remove the underlying test scripts. Continue?`
        )) return;
        await bulkDelete(selectedIdsArray);
        clearSelection();
    }

    async function handleMoveToFolder(targetFolderId: string) {
        await moveToFolder(selectedIdsArray, targetFolderId);
        clearSelection();
    }

    async function handleEditSeverity(severity: string) {
        await bulkPatch(selectedIdsArray, { severity });
        clearSelection();
    }

    async function handleEditTags(tags: string[]) {
        await bulkPatch(selectedIdsArray, { tags });
        clearSelection();
    }

    // ── Folder count badges ─────────────────────────────────────────────────
    const folderCountMap = useMemo(() => {
        const map = new Map<string, number>();
        allTestCases.forEach((tc) => {
            if (tc.folderId) map.set(tc.folderId, (map.get(tc.folderId) ?? 0) + 1);
        });
        return map;
    }, [allTestCases]);

    // ── Drag-and-drop ────────────────────────────────────────────────────────

    // Tracks which test-case row the pointer is currently over and at which edge.
    // Passed down to TestCaseList so it can render the blue insertion line.
    const [dragOverId,       setDragOverId]       = useState<string | null>(null);
    const [dragDropPosition, setDragDropPosition] = useState<'before' | 'after' | null>(null);

    function handleDragOver({ overId, overType, dropPosition }: DragOverPayload) {
        if (overType === 'testCase') {
            setDragOverId(overId);
            setDragDropPosition(dropPosition);
        } else {
            setDragOverId(null);
            setDragDropPosition(null);
        }
    }

    async function handleDragEnd({ activeId, overId, activeType, overType, dropPosition }: DragEndPayload) {
        // State is already cleared by DndProvider before this fires.
        if (!overId) return;

        if (activeType === 'folder') {
            // Folders can only nest inside other folders, not onto test case rows.
            if (overType !== 'folder') return;
            const folderId = activeId.replace(/^folder:/, '');
            if (folderId !== overId) await moveFolder(folderId, overId);
            return;
        }

        if (overType === 'testCase') {
            if (isReorderMode) {
                // Sortable mode: derive position from array indices instead of
                // pointer geometry, since sortable reports the displaced item.
                const oldIdx = testCases.findIndex((tc) => tc._id === activeId);
                const newIdx = testCases.findIndex((tc) => tc._id === overId);
                if (oldIdx === -1 || newIdx === -1 || oldIdx === newIdx) return;
                await reorder(activeId, overId, newIdx > oldIdx ? 'after' : 'before');
            } else {
                await reorder(activeId, overId, dropPosition ?? 'after');
            }
            return;
        }

        // Dropped on a folder → move test case(s) into that folder.
        const idsToMove = selectedIds.has(activeId) ? [...selectedIds] : [activeId];
        await moveToFolder(idsToMove, overId);
        clearSelection();
    }

    // ── AI Suite Generation modal ────────────────────────────────────────────
    const [isAiModalOpen,     setIsAiModalOpen]     = useState(false);
    const [aiFeature,         setAiFeature]          = useState('');
    const [isGeneratingSuite, setIsGeneratingSuite]  = useState(false);
    const [aiSuiteError,      setAiSuiteError]       = useState<string | null>(null);
    const [successMessage,    setSuccessMessage]     = useState<string | null>(null);

    function showSuccess(msg: string) {
        setSuccessMessage(msg);
        setTimeout(() => setSuccessMessage(null), 4000);
    }

    async function handleGenerateSuite() {
        if (!aiFeature.trim() || !effectiveProjectId) return;
        setIsGeneratingSuite(true);
        setAiSuiteError(null);
        try {
            const { data: genData } = await axios.post(
                `${API_URL}/api/ai/generate-test-suite`,
                { feature: aiFeature.trim() },
                { headers: { Authorization: `Bearer ${token}` } },
            );
            if (!genData.success || !Array.isArray(genData.data?.testCases)) {
                setAiSuiteError(genData.error ?? 'AI generation failed.');
                return;
            }
            await axios.post(
                `${API_URL}/api/test-cases/bulk`,
                { projectId: effectiveProjectId, testCases: genData.data.testCases, folderId: selectedFolderId ?? undefined },
                { headers: { Authorization: `Bearer ${token}` } },
            );
            queryClient.invalidateQueries({ queryKey: ['test-cases'] });
            setIsAiModalOpen(false);
            setAiFeature('');
            showSuccess(`Generated ${genData.data.testCases.length} test cases for "${aiFeature.trim()}"!`);
        } catch (err: unknown) {
            const msg = axios.isAxiosError(err) ? err.response?.data?.error ?? err.message : 'Failed to generate suite.';
            setAiSuiteError(msg);
        } finally {
            setIsGeneratingSuite(false);
        }
    }

    // ── Spec-to-Test modal ───────────────────────────────────────────────────
    const [isSpecModalOpen, setIsSpecModalOpen] = useState(false);

    // ── AI Optimizer ─────────────────────────────────────────────────────────
    const [setupModalOpen,     setSetupModalOpen]     = useState(false);
    const [optimizerModalOpen, setOptimizerModalOpen] = useState(false);
    const [optimizerLoading,   setOptimizerLoading]   = useState(false);
    const [optimizerStyle,     setOptimizerStyle]     = useState<WritingStyle>('bdd');
    const [optimizedCases,     setOptimizedCases]     = useState<IOptimizedTestCase[]>([]);

    async function handleStartOptimization(style: WritingStyle) {
        setSetupModalOpen(false);
        setOptimizerStyle(style);
        setOptimizedCases([]);
        setOptimizerLoading(true);
        setOptimizerModalOpen(true);
        try {
            const { data } = await axios.post(
                `${API_URL}/api/ai/optimize-test-cases`,
                { testCaseIds: selectedManualIds, style },
                { headers: { Authorization: `Bearer ${token}` } },
            );
            if (!data.success || !Array.isArray(data.data?.optimizedCases)) throw new Error(data.error ?? 'AI optimization failed');
            setOptimizedCases(data.data.optimizedCases as IOptimizedTestCase[]);
        } catch (err: unknown) {
            const msg = axios.isAxiosError(err) ? err.response?.data?.error ?? err.message : 'Failed to optimize.';
            setOptimizerModalOpen(false);
            showSuccess(`Error: ${msg}`);
        } finally {
            setOptimizerLoading(false);
        }
    }

    // Lookup for OptimizedTestCasesModal
    const testCasesLookup = useMemo(
        () => new Map(allTestCases.map((tc) => [tc._id, {
            title:         tc.title,
            type:          tc.type,
            description:   tc.description,
            suite:         tc.suite,
            preconditions: tc.preconditions,
            severity:  tc.severity,
            caseType:  tc.caseType,
            tags:      tc.tags,
            folderId:  tc.folderId,
        }])),
        [allTestCases],
    );

    // ── CSV export ───────────────────────────────────────────────────────────
    function exportCsv(cases: typeof allTestCases, filename: string) {
        const headers = ['ID', 'Title', 'Suite', 'Severity', 'Type'];
        const rows    = cases.map((tc) => [
            `"${tc.tcId ?? tc._id}"`,
            `"${tc.title.replace(/"/g, '""')}"`,
            `"${tc.suite ?? 'Uncategorized'}"`,
            `"${tc.severity ?? 'MEDIUM'}"`,
            `"${tc.type}"`,
        ].join(','));
        const csv  = [headers.join(','), ...rows].join('\n');
        const blob = new Blob([csv], { type: 'text/csv;charset=utf-8;' });
        const url  = URL.createObjectURL(blob);
        const a    = document.createElement('a');
        a.href = url; a.setAttribute('download', filename);
        document.body.appendChild(a); a.click();
        document.body.removeChild(a); URL.revokeObjectURL(url);
    }

    // ── Fullscreen modal ─────────────────────────────────────────────────────
    function handleExpand()    { setDetailMode('fullscreen'); }
    function handleCollapse()  { setDetailMode('inline'); }

    // ── Keyboard shortcuts ───────────────────────────────────────────────────
    useEffect(() => {
        function onKeyDown(e: KeyboardEvent) {
            const active  = document.activeElement;
            const isInput = active?.tagName === 'INPUT' || active?.tagName === 'TEXTAREA' || active?.tagName === 'SELECT';
            // F — fullscreen toggle
            if (e.key === 'f' && !isInput && selectedTestCaseId && selectedTestCaseId !== NEW_TEST_CASE_ID) {
                setDetailMode(detailMode === 'inline' ? 'fullscreen' : 'inline');
            }
            // Escape — close detail or exit fullscreen
            if (e.key === 'Escape') {
                if (isFullscreen) handleCollapse();
                else if (selectedTestCaseId && !isInput) setSelectedTestCaseId(null);
            }
        }
        document.addEventListener('keydown', onKeyDown);
        return () => document.removeEventListener('keydown', onKeyDown);
    }, [detailMode, selectedTestCaseId, isFullscreen, isViewer, effectiveProjectId]);

    // ── Render ────────────────────────────────────────────────────────────────

    const showDetail = !!selectedTestCaseId;

    return (
        <div data-testid="test-cases-page" className="flex flex-col h-full overflow-hidden">

            {/* ── Toolbar ─────────────────────────────────────────────────── */}
            <div data-testid="test-cases-toolbar" className="flex items-center gap-2 px-3 py-2.5 border-b border-slate-200 dark:border-gh-border-dark bg-white dark:bg-gh-bg-dark flex-shrink-0 flex-wrap">

                {/* Mobile: open folder drawer */}
                <button
                    data-testid="toolbar-folder-drawer-button"
                    aria-label="Browse folders"
                    className="md:hidden flex items-center justify-center w-8 h-8 rounded-lg text-slate-500 hover:bg-slate-100 dark:hover:bg-slate-700 transition-colors flex-shrink-0"
                    onClick={() => setMobileFolderOpen(true)}
                    title="Browse folders"
                >
                    <PanelLeft size={16} />
                </button>

                {/* Page title */}
                <div className="flex items-center gap-1.5 mr-1">
                    <ClipboardList size={16} className="text-blue-500 flex-shrink-0" />
                    <h1 className="text-sm font-semibold text-slate-800 dark:text-slate-100 whitespace-nowrap">Test Cases</h1>
                </div>

                {/* Project selector */}
                {projects.length > 0 && (
                    <select
                        aria-label="Select project"
                        data-testid="toolbar-project-select"
                        value={selectedProjectId}
                        onChange={(e) => setSelectedProjectId(e.target.value)}
                        className="text-xs border border-slate-200 dark:border-gh-border-dark rounded-lg px-2.5 py-1.5 bg-white dark:bg-gh-bg-dark text-slate-700 dark:text-slate-200 focus:outline-none focus:ring-2 focus:ring-blue-500 transition max-w-[160px] truncate"
                    >
                        <option value="">All Projects</option>
                        {projects.map((p) => (
                            <option key={p.id} value={p.id}>{p.name}</option>
                        ))}
                    </select>
                )}

                {/* Search */}
                <div className="relative flex-1 min-w-[140px] max-w-xs">
                    <Search size={13} className="absolute left-2.5 top-1/2 -translate-y-1/2 text-slate-400 pointer-events-none" />
                    <input
                        type="text"
                        aria-label="Search test cases"
                        data-testid="toolbar-search-input"
                        value={searchInput}
                        onChange={(e) => handleSearchChange(e.target.value)}
                        placeholder="Search…"
                        className="w-full pl-7 pr-7 py-1.5 text-xs border border-slate-200 dark:border-gh-border-dark rounded-lg bg-white dark:bg-gh-bg-dark text-slate-800 dark:text-slate-200 placeholder-slate-400 focus:outline-none focus:ring-2 focus:ring-blue-500 transition"
                    />
                    {searchInput && (
                        <button
                            aria-label="Clear search"
                            data-testid="toolbar-search-clear"
                            onClick={() => handleSearchChange('')}
                            className="absolute right-2 top-1/2 -translate-y-1/2 text-slate-400 hover:text-slate-600"
                        >
                            <X size={11} />
                        </button>
                    )}
                </div>

                <div className="flex-1 hidden sm:block" />

                {/* Download all CSV */}
                {allTestCases.length > 0 && (
                    <button
                        data-testid="toolbar-export-csv"
                        onClick={() => exportCsv(allTestCases, 'test-cases-all.csv')}
                        className="hidden sm:flex items-center gap-1.5 px-2.5 py-1.5 text-xs font-medium text-slate-600 dark:text-slate-300 border border-slate-200 dark:border-gh-border-dark rounded-lg hover:bg-slate-50 dark:hover:bg-slate-700 transition-colors"
                    >
                        <Download size={13} /> Export
                    </button>
                )}

                {/* Spec-to-Test */}
                {!isViewer && aiFeatures?.specToTest && effectiveProjectId && (
                    <button
                        data-testid="toolbar-spec-to-test-button"
                        onClick={() => setIsSpecModalOpen(true)}
                        className="hidden sm:flex items-center gap-1.5 px-2.5 py-1.5 text-xs font-medium text-violet-700 dark:text-violet-400 border border-violet-200 dark:border-violet-800 rounded-lg hover:bg-violet-50 dark:hover:bg-violet-900/20 transition-colors"
                    >
                        <Sparkles size={13} /> Spec to Test
                    </button>
                )}

                {/* AI Generate Suite */}
                {!isViewer && aiFeatures?.rootCauseAnalysis !== undefined && effectiveProjectId && (
                    <button
                        data-testid="toolbar-ai-generate-button"
                        onClick={() => setIsAiModalOpen(true)}
                        className="hidden sm:flex items-center gap-1.5 px-2.5 py-1.5 text-xs font-semibold bg-violet-600 hover:bg-violet-500 text-white rounded-lg transition-colors"
                    >
                        <Sparkles size={13} /> AI Generate
                    </button>
                )}

                {/* New test case */}
                {!isViewer && effectiveProjectId && (
                    <button
                        data-testid="toolbar-new-test-case-button"
                        onClick={() => setSelectedTestCaseId(NEW_TEST_CASE_ID)}
                        className="flex items-center gap-1.5 px-2.5 py-1.5 text-xs font-semibold bg-blue-600 hover:bg-blue-500 text-white rounded-lg transition-colors flex-shrink-0"
                    >
                        <Plus size={13} /> New
                    </button>
                )}
            </div>

            {/* ── Success banner ──────────────────────────────────────────── */}
            {successMessage && (
                <div className="mx-4 mt-2 px-3 py-2 bg-emerald-50 dark:bg-emerald-900/20 text-emerald-700 dark:text-emerald-400 text-xs rounded-lg border border-emerald-200 dark:border-emerald-800 flex-shrink-0">
                    {successMessage}
                </div>
            )}

            {/* ── Content area ─────────────────────────────────────────────── */}
            <DndProvider onDragEnd={handleDragEnd} onDragOver={handleDragOver}>
                <div className="flex flex-1 overflow-hidden relative">

                    {/* ── Mobile: Folder Drawer ───────────────────────────── */}
                    {mobileFolderOpen && (
                        <div className="fixed inset-0 z-40 md:hidden flex">
                            {/* Scrim */}
                            <div
                                className="absolute inset-0 bg-black/50"
                                onClick={() => setMobileFolderOpen(false)}
                            />
                            {/* Drawer panel */}
                            <div className="relative w-72 max-w-[85vw] h-full bg-white dark:bg-gh-bg-dark shadow-2xl flex flex-col animate-slideInLeft">
                                {/* Drawer header */}
                                <div className="flex items-center justify-between px-3 py-2.5 border-b border-slate-200 dark:border-gh-border-dark flex-shrink-0">
                                    <span className="text-sm font-semibold text-slate-700 dark:text-slate-200">Folders</span>
                                    <button
                                        data-testid="mobile-folder-drawer-close"
                                        aria-label="Close folder drawer"
                                        onClick={() => setMobileFolderOpen(false)}
                                        className="p-1.5 rounded-lg text-slate-400 hover:text-slate-600 hover:bg-slate-100 dark:hover:bg-slate-700 transition-colors"
                                    >
                                        <X size={16} />
                                    </button>
                                </div>
                                {/*
                                 * FolderTree is normally hidden on mobile (`hidden md:flex`).
                                 * forceVisible=true overrides this so it shows in the drawer.
                                 * We suppress the aside's w-60 by making the parent full-width;
                                 * the aside is flex-shrink-0 so we wrap in overflow-hidden flex.
                                 */}
                                <div className="flex-1 overflow-hidden flex flex-col">
                                    <FolderTree
                                        projectId={effectiveProjectId || null}
                                        forceVisible={true}
                                        folderCountMap={folderCountMap}
                                    />
                                </div>
                            </div>
                        </div>
                    )}

                    {/* ── Desktop: Folder pane (hidden on mobile) ──────────── */}
                    <FolderTree
                        projectId={effectiveProjectId || null}
                        folderCountMap={folderCountMap}
                    />

                    {/* ── Middle: Filter bar + Test case list ──────────────── */}
                    <div className="flex flex-col flex-1 min-w-0 overflow-hidden">
                        <TestCaseFilterBar allTestCases={allTestCases} />
                        <TestCaseList
                            testCases={testCases}
                            isLoading={isLoading}
                            isError={isError}
                            projectId={effectiveProjectId || null}
                            folderId={selectedFolderId}
                            onInvalidate={invalidate}
                            dragOverId={dragOverId}
                            dropPosition={dragDropPosition}
                            isReorderMode={isReorderMode}
                            onToggleReorderMode={() => setIsReorderMode((v) => !v)}
                        />
                    </div>

                    {/* ── Detail pane ──────────────────────────────────────── */}
                    {showDetail && !isFullscreen && (
                        /*
                         * Mobile  → fixed inset-0, z-30, slides in from right.
                         * Desktop → relative inline panel, no z-index needed.
                         * Single React instance to avoid duplicated form state.
                         */
                        <div className="fixed inset-0 z-30 flex animate-slideInRight md:animate-none md:relative md:inset-auto md:z-auto md:flex-shrink-0">
                            <TestCaseDetail
                                testCase={selectedTestCase}
                                projectId={effectiveProjectId || null}
                                folderId={selectedFolderId}
                                onExpand={handleExpand}
                                onClose={() => setSelectedTestCaseId(null)}
                                activeTab={activeTab}
                                onTabChange={setActiveTab}
                            />
                        </div>
                    )}
                </div>
            </DndProvider>

            {/* ── Bulk actions bar ────────────────────────────────────────── */}
            {selectedIds.size > 0 && (
                <BulkActionsBar
                    count={selectedIds.size}
                    onClear={clearSelection}
                    onDelete={isViewer ? undefined : handleBulkDelete}
                    onOptimize={aiFeatures?.testOptimizer && selectedManualIds.length > 0 && !isViewer
                        ? async () => { if (selectedManualIds.length > 0) setSetupModalOpen(true); }
                        : undefined
                    }
                    showOptimize={!!aiFeatures?.testOptimizer && selectedManualIds.length > 0}
                    onExportCSV={() => exportCsv(allTestCases.filter((tc) => selectedIds.has(tc._id)), 'test-cases-selected.csv')}
                    onMoveToFolder={!isViewer ? handleMoveToFolder : undefined}
                    folders={folders}
                    onEditSeverity={!isViewer ? handleEditSeverity : undefined}
                    onEditTags={!isViewer ? handleEditTags : undefined}
                />
            )}

            {/* ── Fullscreen detail modal ─────────────────────────────────── */}
            <TestCaseFullScreenModal
                isOpen={isFullscreen && !!selectedTestCaseId}
                onClose={handleCollapse}
                testCase={selectedTestCase}
                projectId={effectiveProjectId || null}
                folderId={selectedFolderId}
                activeTab={activeTab}
                onTabChange={setActiveTab}
            />

            {/* ── AI Suite Generation modal ───────────────────────────────── */}
            {isAiModalOpen && (
                <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 backdrop-blur-sm p-4">
                    <div data-testid="ai-suite-modal" className="bg-white dark:bg-slate-800 rounded-2xl shadow-2xl border border-slate-200 dark:border-gh-border-dark p-6 w-full max-w-md">
                        <div className="flex items-center gap-2 mb-4">
                            <Sparkles size={18} className="text-violet-500" />
                            <h2 className="text-base font-semibold text-slate-800 dark:text-slate-100">AI Suite Generator</h2>
                        </div>
                        <p className="text-sm text-slate-500 dark:text-slate-400 mb-4">
                            Describe a feature or user story and Agnox AI will generate a full test suite for it.
                        </p>
                        <textarea
                            aria-label="Feature description for AI test suite generation"
                            data-testid="ai-suite-prompt"
                            value={aiFeature}
                            onChange={(e) => setAiFeature(e.target.value)}
                            rows={3}
                            placeholder="e.g. User login with email and password, including edge cases…"
                            className="w-full px-3 py-2 text-sm border border-slate-200 dark:border-gh-border-dark rounded-lg bg-white dark:bg-gh-bg-dark text-slate-800 dark:text-slate-200 placeholder-slate-400 focus:outline-none focus:ring-2 focus:ring-violet-500 resize-none mb-3"
                        />
                        {aiSuiteError && (
                            <p className="text-xs text-rose-500 mb-3">{aiSuiteError}</p>
                        )}
                        <div className="flex gap-2">
                            <button
                                data-testid="ai-suite-generate-button"
                                onClick={handleGenerateSuite}
                                disabled={!aiFeature.trim() || isGeneratingSuite || !effectiveProjectId}
                                className="flex-1 flex items-center justify-center gap-1.5 py-2 text-sm font-semibold bg-violet-600 hover:bg-violet-500 text-white rounded-lg disabled:opacity-50 transition-colors"
                            >
                                {isGeneratingSuite
                                    ? <><Loader2 size={14} className="animate-spin" /> Generating…</>
                                    : <><Sparkles size={14} /> Generate Suite</>
                                }
                            </button>
                            <button
                                onClick={() => { setIsAiModalOpen(false); setAiFeature(''); setAiSuiteError(null); }}
                                className="px-4 py-2 text-sm text-slate-500 hover:text-slate-700 dark:hover:text-slate-300 rounded-lg hover:bg-slate-100 dark:hover:bg-slate-700 transition-colors"
                            >
                                Cancel
                            </button>
                        </div>
                    </div>
                </div>
            )}

            {/* ── Spec-to-Test modal ──────────────────────────────────────── */}
            {isSpecModalOpen && effectiveProjectId && (
                <SpecToTestModal
                    isOpen={isSpecModalOpen}
                    onClose={() => setIsSpecModalOpen(false)}
                    projectId={effectiveProjectId}
                    onSaved={() => {
                        setIsSpecModalOpen(false);
                        invalidate();
                    }}
                />
            )}

            {/* ── AI Optimizer modals ─────────────────────────────────────── */}
            <OptimizeSetupModal
                isOpen={setupModalOpen}
                testCaseCount={selectedManualIds.length}
                onCancel={() => setSetupModalOpen(false)}
                onConfirm={handleStartOptimization}
            />
            <OptimizedTestCasesModal
                isOpen={optimizerModalOpen}
                isLoading={optimizerLoading}
                optimizedCases={optimizedCases}
                testCasesLookup={testCasesLookup}
                writingStyle={optimizerStyle}
                onClose={() => { setOptimizerModalOpen(false); clearSelection(); }}
                onApplied={() => invalidate()}
            />
        </div>
    );
}
